import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAfterIdle } from '@/hooks/useAfterIdle';
import { etagFetch, readEtagCache } from '@/lib/etagFetch';

export type Language = 'de' | 'en';

interface TranslationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
  tMobile: (key: string, fallback?: string) => string;
  isLoading: boolean;
  availableLanguages: { code: Language; name: string }[];
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const availableLanguages: { code: Language; name: string }[] = [
  { code: 'de', name: 'Deutsch' },
  { code: 'en', name: 'English' },
];

// Default translations for fallback (will be overwritten by database)
const defaultTranslations: Record<Language, Record<string, string>> = {
  de: {},
  en: {},
};

interface TranslationProviderProps {
  children: ReactNode;
  initialLanguage?: Language;
}

export const TranslationProvider = ({ children, initialLanguage = 'de' }: TranslationProviderProps) => {
  const [language, setLanguageState] = useState<Language>(initialLanguage);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isIdle = useAfterIdle();

  const applyRows = useCallback((rows: Array<{ key: string; value: string }> | null, lang: Language) => {
    if (!rows) return;
    const map: Record<string, string> = {};
    for (const r of rows) map[r.key] = r.value;
    setTranslations({ ...defaultTranslations[lang], ...map });
    setIsLoading(false);
  }, []);

  // 1. Sync hydrate from localStorage cache (no network, no waiting).
  useEffect(() => {
    const cached = readEtagCache<Array<{ key: string; value: string }>>('translations', language);
    if (cached) applyRows(cached, language);
    else setIsLoading(true);
  }, [language, applyRows]);

  // 2. After idle, revalidate via ETag/If-None-Match (304 = no body).
  useEffect(() => {
    if (!isIdle) return;
    let cancelled = false;
    etagFetch<Array<{ key: string; value: string }>>('translations', language)
      .then((rows) => {
        if (cancelled) return;
        applyRows(rows, language);
      })
      .catch(() => setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isIdle, language, applyRows]);

  // Change language and update URL
  const setLanguage = useCallback((newLang: Language) => {
    if (newLang === language) return;

    setLanguageState(newLang);

    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(de|en)/, '') || '/';

    // URL mappings between German and English
    const deToEnMapping: Record<string, string> = {
      '/system': '/system',
      '/ansatz': '/about',
      '/programme': '/speaking',
      '/kontakt': '/contact',
      '/kurse': '/courses',
      '/buch': '/book',
      '/wissen': '/knowledge',
    };
    const enToDeMapping: Record<string, string> = {
      '/system': '/system',
      '/about': '/ansatz',
      '/speaking': '/programme',
      '/contact': '/kontakt',
      '/courses': '/kurse',
      '/book': '/buch',
      '/knowledge': '/wissen',
    };

    const navigateWithPrefix = (path: string) => {
      if (newLang === 'de') {
        // Map English path to German path
        const mappedPath = enToDeMapping[path] || path;
        navigate(mappedPath);
      } else {
        // Map German path to English path
        const mappedPath = deToEnMapping[path] || path;
        navigate(`/en${mappedPath === '/' ? '' : mappedPath}`);
      }
    };

    // Special case: Blog detail pages use language-specific slugs.
    // When switching languages, we must map the current post_id to the target language slug.
    if (pathWithoutLang.startsWith('/blog/')) {
      const currentSlug = pathWithoutLang.split('/')[2] || '';

      (async () => {
        try {
          const { data: currentTranslation, error: currentError } = await supabase
            .from('blog_post_translations')
            .select('post_id')
            .eq('slug', currentSlug)
            .eq('language', language)
            .maybeSingle();

          if (currentError) throw currentError;
          if (!currentTranslation?.post_id) {
            navigateWithPrefix(pathWithoutLang);
            return;
          }

          const { data: targetTranslation, error: targetError } = await supabase
            .from('blog_post_translations')
            .select('slug')
            .eq('post_id', currentTranslation.post_id)
            .eq('language', newLang)
            .maybeSingle();

          if (targetError) throw targetError;

          if (targetTranslation?.slug) {
            navigateWithPrefix(`/blog/${targetTranslation.slug}`);
          } else {
            // Fallback: keep the same slug if we can't find a translation
            navigateWithPrefix(pathWithoutLang);
          }
        } catch (e) {
          // Fallback to the standard prefix switching
          navigateWithPrefix(pathWithoutLang);
        }
      })();

      return;
    }

    // Default behavior for all other pages
    navigateWithPrefix(pathWithoutLang);
  }, [language, location.pathname, navigate]);

  // Translation function
  const t = useCallback((key: string, fallback?: string): string => {
    return translations[key] || fallback || key;
  }, [translations]);

  // Mobile-aware translation function - uses _mobile suffix if available
  const tMobile = useCallback((key: string, fallback?: string): string => {
    const mobileKey = `${key}_mobile`;
    // Return mobile version if it exists, otherwise fall back to regular version
    if (translations[mobileKey]) {
      return translations[mobileKey];
    }
    return translations[key] || fallback || key;
  }, [translations]);

  return (
    <TranslationContext.Provider value={{ language, setLanguage, t, tMobile, isLoading, availableLanguages }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslations = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslations must be used within a TranslationProvider');
  }
  return context;
};

// Hook to detect language from URL
export const useLanguageFromUrl = (): Language => {
  const location = useLocation();
  const match = location.pathname.match(/^\/(de|en)/);
  if (match) {
    return match[1] as Language;
  }
  return 'de'; // Default to German
};